import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { LogOut, ChevronDown, ChevronUp, Eye } from "lucide-react";
import {
  apiGetTeamOffboardingRequests,
  apiAcknowledgeOffboardingRequest,
  OffboardingRequest,
} from "@/lib/api";
import { useSearchParams } from "react-router-dom";

const PAGE_SIZE = 10;

const STATUS_STYLES: Record<string, string> = {
  pending_manager: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  pending_hr: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  active: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  revoked: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

function StatusBadge({ status }: { status: string }) {
  const label = status.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] ?? ""}`}>
      {label}
    </span>
  );
}

export function OffboardingRequestsWidget() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<OffboardingRequest | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | "view" | null>(null);
  const [managerNotes, setManagerNotes] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["team-offboarding-requests"],
    queryFn: apiGetTeamOffboardingRequests,
  });

  useEffect(() => {
    const openId = searchParams.get("openOffboarding");
    if (openId && requests.length > 0) {
      const req = requests.find((r) => String(r.id) === openId);
      if (req && !selectedRequest) {
        setSelectedRequest(req);
        setActionType(req.status === "pending_manager" ? "approve" : "view");
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("openOffboarding");
          return newParams;
        }, { replace: true });
      }
    }
  }, [searchParams, requests, selectedRequest, setSearchParams]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!selectedRequest || !actionType || actionType === "view") return;
      return apiAcknowledgeOffboardingRequest(selectedRequest.id, managerNotes, actionType as "approve" | "reject");
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      queryClient.invalidateQueries({ queryKey: ["team-offboarding-requests"] });
      setSelectedRequest(null);
      setActionType(null);
      setManagerNotes("");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to process request");
    },
  });

  if (isLoading || requests.length === 0) return null;

  const visibleRequests = showAll ? requests : requests.slice(0, PAGE_SIZE);
  const hasMore = requests.length > PAGE_SIZE;

  const closeDialog = () => { setSelectedRequest(null); setActionType(null); setManagerNotes(""); };

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-900/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <LogOut className="h-4 w-4 text-amber-500" />
            Team Exit Requests
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {requests.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {visibleRequests.map((req) => (
            <div key={req.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-3 rounded-lg bg-muted/50 border">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate">{req.employee_name || `Employee #${req.employee_id}`}</p>
                  <StatusBadge status={req.status} />
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-1">
                  <span>Requested: {format(new Date(req.preferred_last_day), "dd MMM yyyy")}</span>
                  {req.actual_last_day && <span>Last Day: {format(new Date(req.actual_last_day), "dd MMM yyyy")}</span>}
                </div>
                {req.reason && <div className="text-xs mt-1 text-muted-foreground truncate">Reason: {req.reason}</div>}
              </div>

              <div className="flex gap-2 shrink-0">
                {req.status === "pending_manager" ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => { setSelectedRequest(req); setActionType("reject"); }}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => { setSelectedRequest(req); setActionType("approve"); }}
                    >
                      Approve
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedRequest(req); setActionType("view"); }}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                )}
              </div>
            </div>
          ))}

          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-1 text-muted-foreground"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? (
                <><ChevronUp className="h-4 w-4 mr-1" /> Show Less</>
              ) : (
                <><ChevronDown className="h-4 w-4 mr-1" /> Show {requests.length - PAGE_SIZE} More</>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Action / Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={(val) => { if (!val) closeDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === "view"
                ? `Exit Request — ${selectedRequest?.employee_name}`
                : `${actionType === "approve" ? "Approve" : "Reject"} Exit Request — ${selectedRequest?.employee_name}`}
            </DialogTitle>
          </DialogHeader>

          {/* View-only detail mode */}
          {actionType === "view" && selectedRequest && (
            <div className="space-y-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Status</p>
                  <StatusBadge status={selectedRequest.status} />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Preferred Last Day</p>
                  <p className="font-medium">{format(new Date(selectedRequest.preferred_last_day), "dd MMM yyyy")}</p>
                </div>
                {selectedRequest.actual_last_day && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Actual Last Day</p>
                    <p className="font-medium">{format(new Date(selectedRequest.actual_last_day), "dd MMM yyyy")}</p>
                  </div>
                )}
                {selectedRequest.reason && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs mb-1">Reason</p>
                    <p className="bg-muted rounded p-2">{selectedRequest.reason}</p>
                  </div>
                )}
                {selectedRequest.manager_notes && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs mb-1">Manager Notes</p>
                    <p className="bg-muted rounded p-2">{selectedRequest.manager_notes}</p>
                  </div>
                )}
                {selectedRequest.hr_notes && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs mb-1">HR Notes</p>
                    <p className="bg-muted rounded p-2">{selectedRequest.hr_notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>{t("common.cancel")}</Button>
              </DialogFooter>
            </div>
          )}

          {/* Approve / Reject mode */}
          {(actionType === "approve" || actionType === "reject") && (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {actionType === "reject" ? "Reason for Rejection" : t("offboardingModule.managerNotes")}
                  </label>
                  <Textarea
                    placeholder={actionType === "reject" ? "Provide a reason for the rejection..." : "Add any notes..."}
                    value={managerNotes}
                    onChange={(e) => setManagerNotes(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>{t("common.cancel")}</Button>
                <Button
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending || (actionType === "reject" && !managerNotes.trim())}
                  className={actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                >
                  {mutation.isPending ? t("common.saving") : actionType === "approve" ? "Approve" : "Reject"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
